import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../../App";
import {
  ArrowLeft, Clock, MapPin, Phone, ExternalLink, ChevronDown, ChevronRight,
  Calendar, TrendingUp, Coffee, Briefcase, CheckCircle2, AlertTriangle,
  Navigation, Pause, Play, Circle,
} from "lucide-react";

const API = "/api";

const WEEKDAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

function formatDateLabel(dateStr) {
  try {
    const d = new Date(dateStr + "T00:00:00");
    const day = WEEKDAYS[d.getDay()];
    return `${day}, ${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.`;
  } catch { return dateStr; }
}

function getInitials(name) {
  return (name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

export default function MitarbeiterZeitanalyse() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [tick, setTick] = useState(0);

  const load = useCallback(async (p) => {
    setLoading(true);
    let url = `${API}/users/${userId}/time-analysis?period=${p}`;
    if (p === "custom" && customFrom && customTo) {
      url += `&date_from=${customFrom}&date_to=${customTo}`;
    }
    try {
      const res = await authFetch(url);
      if (res.ok) {
        const d = await res.json();
        setData(d);
        if (p === "today" && d.days?.length) setExpandedDay(d.days[0].date);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, [userId, customFrom, customTo]);

  useEffect(() => { load(period); }, [period, load]);

  // Live tick for active timer
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  const switchPeriod = (p) => {
    setPeriod(p);
    setExpandedDay(null);
  };

  if (!data && !loading) return <div className="min-h-screen bg-[#0f1117] flex items-center justify-center text-gray-500">Fehler beim Laden</div>;

  const user = data?.user || {};
  const status = data?.status || {};
  const kpi = data?.kpi || {};
  const days = data?.days || [];

  // Live duration
  const liveSec = status.live_seconds ? status.live_seconds + tick : 0;
  const liveH = Math.floor(liveSec / 3600);
  const liveM = Math.floor((liveSec % 3600) / 60);
  const liveS = liveSec % 60;

  return (
    <div className="min-h-screen bg-[#0f1117] text-white pb-24" data-testid="zeit-analyse-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#0f1117]/95 backdrop-blur-md border-b border-white/5">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="zeit-back-btn">
            <ArrowLeft className="w-5 h-5 text-gray-400" />
          </button>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
              status.is_active ? "bg-emerald-500/20 text-emerald-400 ring-2 ring-emerald-500/30" :
              "bg-gray-700 text-gray-400"
            }`} data-testid="zeit-avatar">
              {getInitials(user.name)}
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold truncate" data-testid="zeit-name">{user.name || "..."}</h1>
              <div className="flex items-center gap-2 text-[10px]">
                {status.is_active ? (
                  <>
                    <span className="flex items-center gap-1 text-emerald-400">
                      <Circle className="w-2 h-2 fill-emerald-400" /> Aktiv
                    </span>
                    {status.current_project && (
                      <span className="text-gray-500 truncate max-w-[120px]">{status.current_project}</span>
                    )}
                  </>
                ) : (
                  <span className="text-gray-500">Nicht eingestempelt</span>
                )}
                {status.gps_status === "missing" && status.is_active && (
                  <span className="text-amber-400 flex items-center gap-0.5">
                    <AlertTriangle className="w-2.5 h-2.5" /> GPS
                  </span>
                )}
              </div>
            </div>
          </div>
          {/* Quick actions */}
          <div className="flex gap-1">
            {user.telefon && (
              <a href={`tel:${user.telefon}`} className="p-2 rounded-lg bg-white/5 hover:bg-white/10" data-testid="zeit-call-btn">
                <Phone className="w-4 h-4 text-gray-400" />
              </a>
            )}
          </div>
        </div>

        {/* Live timer bar */}
        {status.is_active && (
          <div className="px-4 pb-2 flex items-center gap-2">
            <div className="flex-1 bg-emerald-500/10 rounded-lg px-3 py-1.5 flex items-center justify-between">
              <span className="text-[10px] text-emerald-400/70 font-medium">
                {status.on_break ? "Pause" : "Laufzeit"}
              </span>
              <span className="font-mono text-sm font-bold text-emerald-400" data-testid="zeit-live-timer">
                {String(liveH).padStart(2, "0")}:{String(liveM).padStart(2, "0")}:{String(liveS).padStart(2, "0")}
              </span>
            </div>
          </div>
        )}

        {/* Period tabs */}
        <div className="px-4 pb-3 flex gap-1.5">
          {[
            { id: "today", label: "Heute" },
            { id: "week", label: "Woche" },
            { id: "month", label: "Monat" },
            { id: "custom", label: "Zeitraum" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => switchPeriod(t.id)}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${
                period === t.id
                  ? "bg-blue-600 text-white"
                  : "bg-white/[0.04] text-gray-500 hover:text-gray-300"
              }`}
              data-testid={`zeit-period-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Custom date picker */}
        {period === "custom" && (
          <div className="px-4 pb-3 flex gap-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="flex-1 bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2 text-xs text-white"
              data-testid="zeit-date-from" />
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="flex-1 bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2 text-xs text-white"
              data-testid="zeit-date-to" />
            <button onClick={() => load("custom")}
              className="px-3 py-2 bg-blue-600 rounded-lg text-xs font-bold"
              data-testid="zeit-date-apply">
              OK
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 pt-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-4 gap-2" data-testid="zeit-kpi-grid">
              <KpiCard icon={Clock} label="Stunden" value={kpi.total_hours || "00:00"} accent="blue" testId="kpi-hours" />
              <KpiCard icon={Calendar} label="Tage" value={kpi.work_days || 0} accent="emerald" testId="kpi-days" />
              <KpiCard icon={TrendingUp} label="Schnitt" value={kpi.avg_per_day || "00:00"} accent="amber" testId="kpi-avg" />
              <KpiCard icon={Coffee} label="Pausen" value={`${kpi.total_break_min || 0}m`} accent="purple" testId="kpi-break" />
            </div>

            {/* Days list */}
            <div className="space-y-2" data-testid="zeit-days-list">
              {days.length === 0 ? (
                <div className="text-center py-8 text-gray-600 text-xs">Keine Eintraege im gewaehlten Zeitraum</div>
              ) : days.map(day => (
                <DayCard
                  key={day.date}
                  day={day}
                  expanded={expandedDay === day.date}
                  onToggle={() => setExpandedDay(expandedDay === day.date ? null : day.date)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, testId }) {
  const colors = {
    blue: "text-blue-400 bg-blue-500/10",
    emerald: "text-emerald-400 bg-emerald-500/10",
    amber: "text-amber-400 bg-amber-500/10",
    purple: "text-purple-400 bg-purple-500/10",
  };
  const [textColor, bgColor] = (colors[accent] || colors.blue).split(" ");
  return (
    <div className={`rounded-xl ${bgColor} p-3 text-center`} data-testid={testId}>
      <Icon className={`w-3.5 h-3.5 mx-auto mb-1 ${textColor}`} />
      <p className={`text-lg font-bold font-mono ${textColor}`}>{value}</p>
      <p className="text-[9px] text-gray-500 uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function DayCard({ day, expanded, onToggle }) {
  const isToday = day.date === new Date().toISOString().split("T")[0];
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid={`zeit-day-${day.date}`}>
      {/* Day header */}
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors" data-testid={`zeit-day-toggle-${day.date}`}>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isToday ? "bg-blue-500/20 text-blue-400" : "bg-white/[0.04] text-gray-500"
        }`}>
          <Calendar className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-white">{formatDateLabel(day.date)}</span>
            {isToday && <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-[9px] font-bold rounded-full">HEUTE</span>}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-[10px] text-gray-500 font-mono">{day.total_net}</span>
            {day.projects?.filter(Boolean).slice(0, 2).map((p, i) => (
              <span key={i} className="text-[9px] text-gray-600 truncate max-w-[100px]">{p}</span>
            ))}
          </div>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-600" />}
      </button>

      {/* Day detail - Timeline */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-white/5" data-testid={`zeit-day-detail-${day.date}`}>
          <div className="relative pl-6">
            {/* Vertical line */}
            <div className="absolute left-[9px] top-2 bottom-2 w-px bg-white/10" />

            {day.entries.map((e, i) => (
              <div key={e.id || i} className="relative mb-3 last:mb-0">
                {/* Clock In */}
                <TimelineEvent
                  time={e.in_time}
                  label={e.is_active ? "Eingestempelt (aktiv)" : "Eingestempelt"}
                  type="in"
                  gps={e.gps_in}
                  project={e.project}
                />

                {/* Break */}
                {e.break_min > 0 && (
                  <TimelineEvent
                    time=""
                    label={`Pause (${e.break_min} Min.)`}
                    type="break"
                    gps="ok"
                  />
                )}

                {/* Clock Out */}
                {e.out_time && (
                  <TimelineEvent
                    time={e.out_time}
                    label="Ausgestempelt"
                    type="out"
                    gps={e.gps_out}
                    summary={`Netto: ${e.net}`}
                  />
                )}

                {e.is_active && (
                  <TimelineEvent
                    time="--:--"
                    label="Noch aktiv..."
                    type="active"
                    gps={e.gps_in}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Day summary */}
          <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[10px]">
            <span className="text-gray-500">Gesamt Brutto: <span className="text-gray-400 font-mono">{day.total_gross}</span></span>
            <span className="text-gray-500">Netto: <span className="text-white font-bold font-mono">{day.total_net}</span></span>
            <span className="text-gray-500">Pause: <span className="text-gray-400">{day.total_break}m</span></span>
          </div>
        </div>
      )}
    </div>
  );
}

function TimelineEvent({ time, label, type, gps, project, summary }) {
  const dotColors = {
    in: "bg-emerald-400",
    out: "bg-red-400",
    break: "bg-amber-400",
    active: "bg-emerald-400 animate-pulse",
  };
  const gpsColors = {
    ok: "text-emerald-500",
    missing: "text-amber-500",
    error: "text-red-500",
  };

  return (
    <div className="flex items-start gap-3 py-1.5 relative">
      {/* Dot */}
      <div className={`absolute left-[-15px] top-[9px] w-[7px] h-[7px] rounded-full ${dotColors[type] || "bg-gray-500"} ring-2 ring-[#0f1117]`} />

      {/* Time */}
      <span className="w-10 text-[11px] font-mono text-gray-400 flex-shrink-0">{time}</span>

      {/* Label */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] text-gray-300">{label}</span>
          {gps && (
            <MapPin className={`w-2.5 h-2.5 ${gpsColors[gps] || "text-gray-600"}`} />
          )}
        </div>
        {project && <span className="text-[9px] text-gray-600 block">{project}</span>}
        {summary && <span className="text-[9px] text-gray-500 font-mono">{summary}</span>}
      </div>
    </div>
  );
}
